import type { ReactNode } from 'react';
import type {
  EthHourlyRuleResponse,
  HourlyPooledRuleResponse,
  PriceRulesResponse,
  RiptideRuleResponse,
} from '../../lib/apiClient';
import { PASS_BAR, type Verdict } from '../../lib/btc15PriceRules';
import { MIN_READABLE_BETS } from '../../lib/forwardRuleSummary';
import {
  POOLED_HOURLY_BAR,
  POOLED_HOURLY_SERIES,
  type PooledHourlySeries,
} from '../../lib/hourlyPooledRule';
import { RIPTIDE_PASS_BAR } from '../../lib/riptideRule';
import { centsSign, formatPercent, formatSignedUsd } from '../../lib/format';
import styles from './ForwardTests.module.css';

const COIN: Record<PooledHourlySeries, string> = {
  KXETHD: 'ETH',
  KXBTCD: 'BTC',
  KXSOLD: 'SOL',
};

const cents = (x: number | null) =>
  x === null ? '—' : `${x >= 0 ? '+' : '−'}${Math.abs(x * 100).toFixed(1)}¢`;

const lockedOn = (iso: string | undefined) =>
  iso
    ? ` on ${new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : '';

/** What a row needs from any of the four scorecards. */
interface TestScore {
  rule: string;
  label: string;
  settled: number;
  winRate: number | null;
  avgAsk: number | null;
  netPerContract: number | null;
  netLow: number | null;
  netHigh: number | null;
  stakePnl: number;
  verdict: Verdict;
}

interface TestRow {
  score: TestScore;
  target: number;
  stakeDollars: number;
  /** Extra facts shown with the numbers, e.g. "61 sold early". */
  extras: string[];
  /** The rule's own description; rules sharing one have it once, above their group. */
  about?: ReactNode;
  fine?: ReactNode;
}

type Status = 'none' | 'early' | Verdict;

function statusOf(score: TestScore): Status {
  if (score.settled === 0) return 'none';
  if (score.verdict !== 'collecting') return score.verdict;
  return score.settled < MIN_READABLE_BETS ? 'early' : 'collecting';
}

/** Decided tests first: they are the news. Then the ones with the most to read. */
const STATUS_ORDER: Record<Status, number> = {
  passing: 0,
  failing: 1,
  collecting: 2,
  early: 3,
  none: 4,
};

const STATUS_LABEL: Record<Status, string> = {
  none: 'No bets yet',
  early: 'Too early',
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
};

function netClass(value: number | null): string {
  if (value === null) return styles.neutral;
  const sign = centsSign(value);
  return sign > 0 ? styles.positive : sign < 0 ? styles.negative : styles.neutral;
}

function Row({ row }: { row: TestRow }) {
  const { score, target } = row;
  const status = statusOf(score);
  // Below MIN_READABLE_BETS a rate is noise: one lost bet is "0.0% win rate".
  const readable = score.settled >= MIN_READABLE_BETS;
  return (
    <li className={styles.row}>
      <details className={styles.details}>
        <summary className={styles.summary}>
          <span className={styles.lineOne}>
            <span className={styles.name}>
              <span className={styles.ruleId}>{score.rule}</span> {score.label}
            </span>
            {readable && (
              <span className={`${styles.net} ${netClass(score.netPerContract)}`}>
                {cents(score.netPerContract)}
              </span>
            )}
          </span>
          <span className={styles.lineTwo}>
            <span>
              {score.settled >= target
                ? `${score.settled.toLocaleString()} bets, target ${target.toLocaleString()} met`
                : `${score.settled.toLocaleString()} of ${target.toLocaleString()} bets`}
              {readable && <> · {formatPercent(score.winRate, 1)} won</>}
            </span>
            <span className={styles[status]}>{STATUS_LABEL[status]}</span>
          </span>
        </summary>
        <div className={styles.body}>
          {readable ? (
            <dl className={styles.stats}>
              <div>
                <dt>Win rate</dt>
                <dd>{formatPercent(score.winRate, 1)}</dd>
              </div>
              <div>
                <dt>Avg price</dt>
                <dd>
                  {score.avgAsk === null ? '—' : `${(score.avgAsk * 100).toFixed(1)}¢`}
                </dd>
              </div>
              <div>
                <dt>Per contract</dt>
                <dd>{cents(score.netPerContract)}</dd>
              </div>
              <div>
                <dt>${row.stakeDollars}/bet P&amp;L</dt>
                <dd>{formatSignedUsd(score.stakePnl)}</dd>
              </div>
            </dl>
          ) : (
            <p className={styles.note}>
              {score.settled === 0
                ? 'No bets settled yet.'
                : `${score.settled.toLocaleString()} of ${target.toLocaleString()} bets settled. Too few for a win rate or a per-contract figure to mean anything.`}
            </p>
          )}
          {readable && score.netLow !== null && score.netHigh !== null && (
            <p className={styles.fine}>
              Likely range per contract: {cents(score.netLow)} to {cents(score.netHigh)}.
            </p>
          )}
          {row.extras.map((extra) => (
            <p key={extra} className={styles.fine}>
              {extra}
            </p>
          ))}
          {row.about && <p className={styles.note}>{row.about}</p>}
          {row.fine && <p className={styles.fine}>{row.fine}</p>}
        </div>
      </details>
    </li>
  );
}

function Unavailable({ name }: { name: string }) {
  return (
    <li className={styles.row}>
      <p className={styles.unavailable}>{name}: forward test data is unavailable.</p>
    </li>
  );
}

/**
 * Every forward paper test on one screen: a two-line row per rule (how far
 * through its fixed bet count, and what it has made per contract), with the
 * full scorecard and the rule's description one tap away. Rules are locked
 * before testing and scored only on what happened after. Paper only;
 * nothing here is executed.
 */
export function ForwardTests({
  priceRules,
  riptide,
  ethHourly,
  pooled,
}: {
  priceRules: { data: PriceRulesResponse | undefined; isError: boolean };
  riptide: { data: RiptideRuleResponse | undefined; isError: boolean };
  ethHourly: { data: EthHourlyRuleResponse | undefined; isError: boolean };
  pooled: { data: HourlyPooledRuleResponse | undefined; isError: boolean };
}) {
  // Two markets, each listed decided-first; a rule's position within its
  // status keeps the order below.
  const btc: Array<{ status: Status; node: ReactNode }> = [];
  const hourly: Array<{ status: Status; node: ReactNode }> = [];

  if (priceRules.data) {
    const data = priceRules.data;
    for (const score of data.summary) {
      btc.push({
        status: statusOf(score),
        node: (
          <Row
            key={score.rule}
            row={{
              score,
              target: PASS_BAR.minBets,
              stakeDollars: data.stakeDollars,
              extras:
                score.rule.startsWith('D') && score.exited > 0
                  ? [`${score.exited.toLocaleString()} sold early.`]
                  : [],
            }}
          />
        ),
      });
    }
  } else if (priceRules.isError) {
    btc.push({
      status: 'none',
      node: <Unavailable key="price-rules" name="BTC 15-minute price rules" />,
    });
  }

  if (riptide.data) {
    const data = riptide.data;
    const checks = data.checks ?? null;
    btc.push({
      status: statusOf(data.summary),
      node: (
        <Row
          key={data.summary.rule}
          row={{
            score: data.summary,
            target: RIPTIDE_PASS_BAR.minBets,
            stakeDollars: data.stakeDollars,
            extras: checks
              ? [
                  `Checked ${checks.checked.toLocaleString()} of ${checks.rounds.toLocaleString()} rounds: ${checks.triggered.toLocaleString()} had a big enough move, ${checks.missed.toLocaleString()} ${checks.missed === 1 ? 'was' : 'were'} missed.`,
                ]
              : [],
            about: (
              <>
                When BTC has just moved 0.70% or more in an hour, bet the next 15-minute
                round goes the other way: one minute in, buy the side against the move if
                it costs under 60¢, and hold to settlement. Over two years of prices the
                next round did go the other way 54–56% of the time, but at real Kalshi
                prices the profit was too small to be sure of. It was locked
                {lockedOn(data.registeredAt)} before this test began and passes after{' '}
                {RIPTIDE_PASS_BAR.minBets} bets with a profit after fees. It only bets
                about six times a day.
              </>
            ),
            fine: (
              <>
                After Kalshi&apos;s taker fee, at the ask recorded that minute. These bets
                cost about 46¢ and win about half the time, so the win rate alone says
                little; the per-contract figure is the one to watch.
              </>
            ),
          }}
        />
      ),
    });
  } else if (riptide.isError) {
    btc.push({ status: 'none', node: <Unavailable key="riptide" name="Riptide" /> });
  }

  if (ethHourly.data) {
    const data = ethHourly.data;
    hourly.push({
      status: statusOf(data.summary),
      node: (
        <Row
          key={data.summary.rule}
          row={{
            score: data.summary,
            target: PASS_BAR.minBets,
            stakeDollars: data.stakeDollars,
            extras: [],
            about: (
              <>
                A different market: Kalshi&apos;s hourly &ldquo;ETH above or below&rdquo;
                contracts. Once the last third of the hour starts, buy whichever side is
                priced 85–95¢ and hold to settlement, at most one bet an hour. It is the
                one rule that still made money on data it was not tuned on, and it was
                locked
                {lockedOn(data.registeredAt)} before this test began. It passes after{' '}
                {PASS_BAR.minBets} bets with a {PASS_BAR.minWinRate * 100}%+ win rate and
                a profit after fees.
              </>
            ),
            fine: (
              <>
                After Kalshi&apos;s taker fee, at the ask recorded that minute. These
                hourly strikes trade thinly, so a real order may not fill at that price; a
                losing bet costs about nine times what a win pays.
              </>
            ),
          }}
        />
      ),
    });
  } else if (ethHourly.isError) {
    hourly.push({
      status: 'none',
      node: <Unavailable key="eth-hourly" name="ETH hourly rule" />,
    });
  }

  if (pooled.data) {
    const data = pooled.data;
    const s = data.summary;
    const target = s.target ?? POOLED_HOURLY_BAR.bets;
    hourly.push({
      status: statusOf(s),
      node: (
        <Row
          key={s.rule}
          row={{
            score: s,
            target,
            stakeDollars: data.stakeDollars,
            extras:
              s.settled > 0
                ? [
                    `By coin: ${POOLED_HOURLY_SERIES.map(
                      (series) =>
                        `${COIN[series]} ${cents(s.bySeries[series].netPerContract)} on ${s.bySeries[series].settled.toLocaleString()}`,
                    ).join(', ')}.`,
                  ]
                : [],
            about: (
              <>
                The ETH hourly rule, run on Kalshi&apos;s hourly ETH, BTC and SOL markets
                together: once the last third of the hour starts, buy whichever side is
                priced 85–95¢ and hold to settlement, at most one bet per coin per hour.
                In research it made about 1¢ a contract in 2026 and nothing in 2025, too
                small to see in a few hundred bets. So this test was locked
                {lockedOn(data.registeredAt)} to run for {target.toLocaleString()} bets,
                and is judged once, on those: it passes only if the whole likely range of
                its profit after fees is above zero.
              </>
            ),
            fine: (
              <>
                After Kalshi&apos;s taker fee, at the ask recorded that minute. The range
                treats bets settled on the same day as moving together. These hourly
                strikes trade thinly, so a real order may not fill at that price; a losing
                bet costs about nine times what a win pays.
              </>
            ),
          }}
        />
      ),
    });
  } else if (pooled.isError) {
    hourly.push({
      status: 'none',
      node: <Unavailable key="pooled" name="Hourly rule, three coins" />,
    });
  }

  const sorted = (rows: Array<{ status: Status; node: ReactNode }>) =>
    [...rows].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);

  return (
    <section className={styles.card} aria-labelledby="btc15-forward-tests-heading">
      <div className={styles.headingRow}>
        <h2 id="btc15-forward-tests-heading" className={styles.heading}>
          Forward tests
        </h2>
        <span className={styles.badge}>Paper</span>
      </div>
      <p className={styles.note}>
        Rules locked before testing and scored only on what happened after, at real Kalshi
        prices. The figure on the right is profit per contract after fees. Decided tests
        are listed first.
      </p>
      {btc.length === 0 && hourly.length === 0 && (
        <p className={styles.note}>Loading forward tests…</p>
      )}
      {btc.length > 0 && (
        <div className={styles.group}>
          <h3 className={styles.groupHeading}>BTC 15-minute rounds</h3>
          {priceRules.data && (
            <details className={styles.groupAbout}>
              <summary>About the C, D and E price rules</summary>
              <p className={styles.note}>
                BTC 15-minute price rules found in recorded Kalshi prices, then locked
                {lockedOn(priceRules.data.registeredAt)} before being tested: buy
                whichever side is priced in a band late in the round and hold to
                settlement (C), or sell early at the bid once it hits a set level (D).
                Only rounds after each rule was locked count. A rule passes after{' '}
                {PASS_BAR.minBets} bets with a {PASS_BAR.minWinRate * 100}%+ win rate and
                a profit after fees.
              </p>
              <p className={styles.fine}>
                After Kalshi&apos;s taker fee, at the ask recorded that minute. Real fills
                can be worse; a losing bet at these prices costs far more than a win pays.
              </p>
            </details>
          )}
          <ul className={styles.rows}>{sorted(btc).map((row) => row.node)}</ul>
        </div>
      )}
      {hourly.length > 0 && (
        <div className={styles.group}>
          <h3 className={styles.groupHeading}>Hourly markets</h3>
          <ul className={styles.rows}>{sorted(hourly).map((row) => row.node)}</ul>
        </div>
      )}
    </section>
  );
}
